import { useEffect, useState, useCallback } from "react";
import { api, authedUrl, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import {
    QrCode as QrIcon,
    TextAa,
    LinkSimple,
    WifiHigh,
    Trash,
    DownloadSimple,
    PencilSimple,
    Plus,
    X,
    Eye,
    EyeSlash,
    FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = {
    label: "",
    type: "text",
    text: "",
    url: "",
    wifi_ssid: "",
    wifi_password: "",
    wifi_security: "WPA",
    wifi_hidden: false,
    fg_color: "#000000",
    bg_color: "#FFFFFF",
    size: 512,
};

export default function QRCodes() {
    const { user } = useAuth();
    const { t } = useT();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // {mode:'create'|'edit', data}

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/qrcodes");
            setItems(data);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Eroare");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onSaved = (newItem, isEdit) => {
        setItems((arr) => (isEdit ? arr.map((x) => (x.id === newItem.id ? newItem : x)) : [newItem, ...arr]));
        setEditing(null);
    };

    const remove = async (it) => {
        if (!window.confirm(`Ștergi codul "${it.label}"?`)) return;
        try {
            await api.delete(`/qrcodes/${it.id}`);
            setItems((arr) => arr.filter((x) => x.id !== it.id));
            toast.success("Șters");
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Eroare");
        }
    };

    const download = (it, fmt = "png") => {
        const a = document.createElement("a");
        a.href = authedUrl(`/qrcodes/${it.id}/download?fmt=${fmt}`);
        a.download = `${it.label || "qrcode"}.${fmt}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className="relative">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="label-mono mb-2">{t("qr_pre")}</div>
                    <h1 className="heading-tight fluid-h1">{t("qr_h1")}</h1>
                </div>
                {isAdmin && (
                    <button
                        onClick={() => setEditing({ mode: "create", data: EMPTY_FORM })}
                        data-testid="btn-new-qr"
                        className="btn-press flex items-center gap-2 px-4 h-11"
                        style={{ background: "var(--text)", color: "var(--bg)" }}
                    >
                        <Plus size={18} />
                        <span className="text-sm font-medium">Nou</span>
                    </button>
                )}
            </div>

            {!isAdmin && (
                <div className="mb-6 p-3 border text-sm" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                    Doar admin poate genera și edita coduri QR.
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="aspect-square tile-shimmer" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="border border-dashed p-10 text-center" style={{ borderColor: "var(--border-strong)" }}>
                    <QrIcon size={48} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                    <div className="label-mono mb-2">empty archive</div>
                    <h3 className="heading-tight text-xl">Niciun cod QR salvat încă</h3>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((it) => (
                        <QRCard
                            key={it.id}
                            item={it}
                            isAdmin={isAdmin}
                            onEdit={() => setEditing({ mode: "edit", data: it })}
                            onDelete={() => remove(it)}
                            onDownload={(fmt) => download(it, fmt)}
                        />
                    ))}
                </div>
            )}

            {editing && (
                <QREditor
                    initial={editing.data}
                    mode={editing.mode}
                    onClose={() => setEditing(null)}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

function QRCard({ item, isAdmin, onEdit, onDelete, onDownload }) {
    const TypeIcon = item.type === "url" ? LinkSimple : item.type === "wifi" ? WifiHigh : TextAa;
    return (
        <div className="border p-4 flex gap-4 card-hover" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={`qr-card-${item.id}`}>
            <div className="shrink-0 w-28 h-28 sm:w-32 sm:h-32 p-2" style={{ background: "#fff" }}>
                <img src={authedUrl(`/qrcodes/${item.id}/image?fmt=png`)} alt={item.label} className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-between">
                <div>
                    <div className="flex items-center gap-1.5 mb-1">
                        <TypeIcon size={12} style={{ color: "var(--text-muted)" }} />
                        <span className="label-mono">{item.type}</span>
                    </div>
                    <div className="heading-tight text-base truncate" style={{ color: "var(--text)" }}>{item.label}</div>
                    <div className="text-xs mt-1 font-mono truncate" style={{ color: "var(--text-muted)" }}>
                        {item.type === "wifi" ? `SSID: ${item.wifi_ssid || "—"}` : item.text || item.url || "—"}
                    </div>
                </div>
                <div className="flex gap-1 mt-3 flex-wrap">
                    <button onClick={() => onDownload("png")} data-testid={`btn-dl-png-${item.id}`} className="btn-press h-8 px-2.5 flex items-center gap-1 border text-xs" style={{ borderColor: "var(--border-strong)" }}>
                        <DownloadSimple size={12} /> PNG
                    </button>
                    <button onClick={() => onDownload("jpg")} data-testid={`btn-dl-jpg-${item.id}`} className="btn-press h-8 px-2.5 flex items-center gap-1 border text-xs" style={{ borderColor: "var(--border-strong)" }}>
                        <DownloadSimple size={12} /> JPG
                    </button>
                    {isAdmin && (
                        <>
                            <button onClick={onEdit} data-testid={`btn-edit-qr-${item.id}`} className="btn-press h-8 w-8 flex items-center justify-center border" style={{ borderColor: "var(--border-strong)" }}>
                                <PencilSimple size={12} />
                            </button>
                            <button onClick={onDelete} data-testid={`btn-delete-qr-${item.id}`} className="btn-press h-8 w-8 flex items-center justify-center" style={{ background: "var(--danger)", color: "#fff" }}>
                                <Trash size={12} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function QREditor({ initial, mode, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...initial }));
    const [busy, setBusy] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [preview, setPreview] = useState(null);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const previewQR = async () => {
        try {
            // Build a temp payload by saving via PUT/POST is heavy; instead, just hit a tiny canvas-side preview via free QR API would leak data.
            // Simpler: rely on save to re-render server-side image. We display nothing until saved.
            setPreview(null);
        } catch {}
    };
    useEffect(() => { previewQR(); }, [form.type, form.text, form.url, form.wifi_ssid, form.wifi_password, form.wifi_security, form.wifi_hidden, form.fg_color, form.bg_color]);

    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            const isEdit = mode === "edit" && initial?.id;
            const { data } = isEdit
                ? await api.put(`/qrcodes/${initial.id}`, payload)
                : await api.post("/qrcodes", payload);
            toast.success(isEdit ? "Cod actualizat" : "Cod generat");
            onSaved(data, isEdit);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Eroare");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-6" style={{ background: "rgba(0,0,0,0.85)" }} onClick={onClose}>
            <div
                className="relative bg-[var(--surface)] border w-full max-w-2xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto"
                style={{ borderColor: "var(--border)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="qr-editor"
            >
                <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 border-b" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                    <div>
                        <div className="label-mono">{mode === "edit" ? "editează" : "nou"}</div>
                        <h2 className="heading-tight text-xl">Cod QR</h2>
                    </div>
                    <button onClick={onClose} data-testid="btn-close-editor" className="btn-press w-9 h-9 flex items-center justify-center border" style={{ borderColor: "var(--border)" }}>
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-5">
                    <Field label="Etichetă">
                        <input
                            data-testid="input-label"
                            value={form.label}
                            onChange={(e) => update({ label: e.target.value })}
                            placeholder="ex: WiFi cabană"
                            className="w-full h-11 px-3 bg-[var(--bg)] outline-none border text-sm"
                            style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                        />
                    </Field>

                    <Field label="Tip">
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { v: "text", icon: TextAa, l: "Text" },
                                { v: "url", icon: LinkSimple, l: "Link" },
                                { v: "wifi", icon: WifiHigh, l: "WiFi" },
                            ].map((t) => (
                                <button
                                    key={t.v}
                                    onClick={() => update({ type: t.v })}
                                    data-testid={`type-${t.v}`}
                                    className="btn-press h-11 flex items-center justify-center gap-2 border"
                                    style={{
                                        borderColor: form.type === t.v ? "var(--text)" : "var(--border)",
                                        background: form.type === t.v ? "var(--surface-2)" : "transparent",
                                        color: "var(--text)",
                                    }}
                                >
                                    <t.icon size={16} />
                                    <span className="text-sm">{t.l}</span>
                                </button>
                            ))}
                        </div>
                    </Field>

                    {form.type === "text" && (
                        <Field label="Text">
                            <textarea
                                data-testid="input-text"
                                rows={4}
                                value={form.text || ""}
                                onChange={(e) => update({ text: e.target.value })}
                                placeholder="Conținutul textului…"
                                className="w-full p-3 bg-[var(--bg)] outline-none border text-sm resize-none"
                                style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                            />
                        </Field>
                    )}

                    {form.type === "url" && (
                        <Field label="Link">
                            <input
                                data-testid="input-url-qr"
                                type="url"
                                value={form.url || ""}
                                onChange={(e) => update({ url: e.target.value })}
                                placeholder="https://…"
                                className="w-full h-11 px-3 bg-[var(--bg)] outline-none border text-sm"
                                style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                            />
                        </Field>
                    )}

                    {form.type === "wifi" && (
                        <div className="space-y-4">
                            <Field label="Nume rețea (SSID)">
                                <input
                                    data-testid="input-ssid"
                                    value={form.wifi_ssid || ""}
                                    onChange={(e) => update({ wifi_ssid: e.target.value })}
                                    className="w-full h-11 px-3 bg-[var(--bg)] outline-none border text-sm"
                                    style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                                />
                            </Field>
                            <Field label="Parolă">
                                <div className="flex items-center border" style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}>
                                    <input
                                        data-testid="input-wifi-pw"
                                        type={showPw ? "text" : "password"}
                                        value={form.wifi_password || ""}
                                        onChange={(e) => update({ wifi_password: e.target.value })}
                                        className="flex-1 h-11 px-3 bg-transparent outline-none text-sm"
                                        style={{ color: "var(--text)" }}
                                    />
                                    <button type="button" onClick={() => setShowPw((v) => !v)} className="px-3 h-11 btn-press" style={{ color: "var(--text-muted)" }}>
                                        {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </Field>
                            <Field label="Securitate">
                                <div className="grid grid-cols-3 gap-2">
                                    {["WPA", "WEP", "nopass"].map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`sec-${s}`}
                                            onClick={() => update({ wifi_security: s })}
                                            className="btn-press h-11 border text-sm"
                                            style={{
                                                borderColor: form.wifi_security === s ? "var(--text)" : "var(--border)",
                                                background: form.wifi_security === s ? "var(--surface-2)" : "transparent",
                                                color: "var(--text)",
                                            }}
                                        >
                                            {s === "nopass" ? "Fără parolă" : s}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--text-muted)" }}>
                                <input
                                    type="checkbox"
                                    data-testid="input-hidden"
                                    checked={!!form.wifi_hidden}
                                    onChange={(e) => update({ wifi_hidden: e.target.checked })}
                                    className="accent-white"
                                />
                                Rețea ascunsă
                            </label>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Culoare cod">
                            <input
                                data-testid="input-fg"
                                type="color"
                                value={form.fg_color}
                                onChange={(e) => update({ fg_color: e.target.value })}
                                className="w-full h-11 bg-transparent border cursor-pointer"
                                style={{ borderColor: "var(--border-strong)" }}
                            />
                        </Field>
                        <Field label="Fundal">
                            <input
                                data-testid="input-bg"
                                type="color"
                                value={form.bg_color}
                                onChange={(e) => update({ bg_color: e.target.value })}
                                className="w-full h-11 bg-transparent border cursor-pointer"
                                style={{ borderColor: "var(--border-strong)" }}
                            />
                        </Field>
                    </div>

                    {preview}
                </div>

                <div className="sticky bottom-0 z-10 px-4 sm:px-6 py-3 border-t flex gap-2 justify-end" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                    <button onClick={onClose} className="btn-press h-11 px-4 border text-sm" style={{ borderColor: "var(--border)" }}>
                        Anulează
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="btn-save-qr"
                        className="btn-press h-11 px-5 flex items-center gap-2 disabled:opacity-50"
                        style={{ background: "var(--text)", color: "var(--bg)" }}
                    >
                        <FloppyDisk size={16} />
                        <span className="text-sm font-medium">{busy ? "se salvează…" : mode === "edit" ? "Regenerează" : "Generează"}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-mono mb-2 block">{label}</label>
            {children}
        </div>
    );
}
